/**
 * Module dependencies.
 */

import { SelectField } from 'app/components/atoms/forms/fields/select/select-field';
import { PaginatedSelectField } from 'app/components/atoms/forms/fields/select/paginated-select-field';
import { searchOnInput } from 'app/core/utils/filter-utils';
import React, { useMemo, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { SelectFieldFormItem } from '../form-types';
import { identity, set } from 'lodash';
import { useParams } from 'react-router-dom';

/**
 * `FormSelectFieldProps` type.
 */

export type FormSelectFieldProps = {
  name: string;
  item: SelectFieldFormItem;
  form: UseFormReturn<FieldValues, any>;
};

/**
 * `ByHook` component.
 */

function ByHook(props: FormSelectFieldProps) {
  const { form, item } = props;
  const { control } = form;
  const { watch } = form;
  const {
    filterOption = searchOnInput,
    showSearch = false,
    options,
    disabled,
    placeholder,
    name,
    label,
    onChange,
    ...rest
  } = item;

  const { hookProps, normalize, hook } = options as any;
  const params = useParams();
  const values = watch();
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const hookPropsParams = hookProps?.(values, params, { searchValue }) ?? [];
  const { data } = hook?.(...hookPropsParams) ?? { data: options };
  const normalizer = normalize ?? identity;
  const optionValues = useMemo(() => normalizer(data?.results ?? data, values), [data, normalizer, values]);

  set(form, ['cache', 'hooks', name], {
    data,
    optionValues
  });

  return (
    <SelectField
      {...rest}
      control={control}
      disabled={typeof disabled === 'function' ? disabled(values, null) : disabled}
      filterOption={filterOption}
      label={label}
      name={name}
      onChange={(value, option) => onChange?.(value, option, form)}
      onSearch={setSearchValue}
      options={optionValues}
      placeholder={placeholder ?? placeholder === false ? undefined : label}
      showSearch={showSearch}
    />
  );
}

/**
 * `ByHookPaginated` component.
 */

function ByHookPaginated(props: FormSelectFieldProps) {
  const { form, item } = props;
  const { control } = form;
  const { watch } = form;
  const {
    filterOption = searchOnInput,
    showSearch = false,
    options,
    disabled,
    placeholder,
    name,
    label,
    onChange,
    ...rest
  } = item;

  const { hookProps, normalize, hook } = options as any;
  const params = useParams();
  const values = watch();
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const hookPropsParams = hookProps?.(values, params, { searchValue }) ?? [];
  const queryResult = hook?.(...hookPropsParams) ?? { data: [] };
  const normalizer = normalize ?? identity;

  // For paginated selects, we expect the hook to return: { data, loadMore, hasNextPage, isFetchingNextPage }
  const data = useMemo(() => queryResult.data ?? [], [queryResult.data]);
  const loadMore = queryResult.loadMore;
  const hasNextPage = queryResult.hasNextPage;
  const isFetchingNextPage = queryResult.isFetchingNextPage;

  const optionValues = useMemo(() => normalizer(data, values), [data, normalizer, values]);

  set(form, ['cache', 'hooks', name], {
    data,
    optionValues
  });

  return (
    <PaginatedSelectField
      {...rest}
      control={control}
      disabled={typeof disabled === 'function' ? disabled(values, null) : disabled}
      filterOption={filterOption}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      label={label}
      name={name}
      onChange={(value, option) => onChange?.(value, option, form)}
      onLoadMore={loadMore}
      onSearch={setSearchValue}
      options={optionValues}
      placeholder={placeholder ?? placeholder === false ? undefined : label}
      showSearch={showSearch}
    />
  );
}

/**
 * `ByStatic` component.
 */

function ByStatic(props: FormSelectFieldProps) {
  const { form, item } = props;
  const { control } = form;
  const {
    label,
    placeholder,
    optionLabelProp,
    filterOption = searchOnInput,
    showSearch = false,
    disabled,
    options,
    onChange,
    ...rest
  } = item;

  return (
    <SelectField
      {...rest}
      control={control}
      disabled={typeof disabled === 'function' ? disabled({}, null) : disabled}
      filterOption={filterOption}
      label={label}
      onChange={(value, option) => onChange?.(value, option, form)}
      optionLabelProp={optionLabelProp}
      options={options as any}
      placeholder={placeholder ?? placeholder === false ? undefined : label}
      showSearch={showSearch}
    />
  );
}

/**
 * Export `FormSelectField` component.
 */

export function FormSelectField(props: FormSelectFieldProps): JSX.Element {
  const { item } = props;

  if ((item.options as any)?.hook) {
    // Check if pagination is enabled
    if (item.paginated?.enabled) {
      return <ByHookPaginated {...props} />;
    }

    return <ByHook {...props} />;
  }

  return <ByStatic {...props} />;
}
